import { useEffect, useState, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, ContactShadows, Environment } from '@react-three/drei';
import { useRef } from 'react';

/**
 * Fighter3D — R3F canvas that loads a .glb species model, rotates it
 * slowly, and casts a real shadow onto the arena floor. Falls back to
 * a rendered image (via <img>) when no model URL is provided so it
 * plugs into the existing 2.5D battle presentation without a rewrite.
 */
function GlbModel({ url, targetSize = 2.5 }) {
  const { scene } = useGLTF(url);
  const ref = useRef();
  useFrame((_state, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.5;
  });
  // Fit the model to a consistent height regardless of source scale.
  useEffect(() => {
    if (!scene || !ref.current) return;
    import('three').then((THREE) => {
      const box = new THREE.Box3().setFromObject(scene);
      const size = new THREE.Vector3();
      box.getSize(size);
      const height = size.y || 1;
      const scale = targetSize / height;
      ref.current.scale.setScalar(scale);
      // Sit on the ground plane
      ref.current.position.y = -box.min.y * scale;
    });
  }, [scene, targetSize]);
  return <primitive ref={ref} object={scene} />;
}

function ModelFallback({ image, color }) {
  // Simple lit sphere while the .glb is loading — matches rarity colour
  // so the placeholder feels intentional.
  return (
    <mesh>
      <sphereGeometry args={[0.9, 32, 32]} />
      <meshStandardMaterial color={color || '#4ade80'} roughness={0.6} metalness={0.1} />
    </mesh>
  );
}

export default function Fighter3D({ modelUrl, imageFallback, rarityColor, facing = 'right', size = 176 }) {
  // If no model URL and no image → nothing to show
  if (!modelUrl && !imageFallback) return null;

  // No 3D model yet — fall through to the existing 2D presentation
  if (!modelUrl) {
    return (
      <img
        src={imageFallback}
        alt=""
        style={{
          width: size,
          height: size * 1.3,
          objectFit: 'contain',
          objectPosition: 'center bottom',
          transform: `scaleX(${facing === 'left' ? -1 : 1})`,
          filter: `drop-shadow(0 12px 10px rgba(0,0,0,0.7)) drop-shadow(0 0 22px ${rarityColor}77) contrast(1.06) saturate(1.1)`,
        }}
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size * 1.3 }}
      data-testid="fighter-3d-canvas"
      data-model-url={modelUrl}
    >
      <Canvas
        shadows
        dpr={[1, 1.5]}
        camera={{ position: [0, 1.2, 3.4], fov: 42 }}
        gl={{ alpha: true, antialias: true }}
      >
        <ambientLight intensity={0.5} />
        <directionalLight
          position={[3, 5, 2]}
          intensity={1.2}
          castShadow
          shadow-mapSize-width={512}
          shadow-mapSize-height={512}
        />
        <Suspense fallback={<ModelFallback color={rarityColor} />}>
          <group rotation={[0, facing === 'left' ? Math.PI : 0, 0]}>
            <GlbModel url={modelUrl} />
          </group>
          <ContactShadows position={[0, -0.001, 0]} opacity={0.75} scale={5} blur={2.4} far={2} />
          <Environment preset="forest" />
        </Suspense>
      </Canvas>
    </div>
  );
}
